// 戦闘の数値はすべてここに集める（mch-dice の config.js が元）。
// 調整は withConfig(overrides, fn) / setConfig(overrides) で上書きできる（シミュレーターの --config）。
// 割合で書いてある値（heal: 0.04 など）は最大HPに対する割合。本作は章ごとに能力値が大きく伸びるため。
import type { BuildingType, ItemId, StatusKey, ChestLoot } from '../types';
import type { AiStyle } from './ai';

/** 敵の武将の傾向：本陣速攻・築城・武将狙い・妖狩り */
export type RivalAiKey = 'rush' | 'builder' | 'hunter' | 'farmer';
/** 傾向ごとの性質（版3。武将の能力に掛ける倍率と技の旗。setup.ts の rivalSpec） */
export interface RivalTraits {
  hp?: number;
  atk?: number;
  /** 速さの加算 */
  agi?: number;
  core?: number;
  hero?: number;
  neutral?: number;
  /** 被ダメの倍率（1より大きければ打たれ弱い） */
  taken?: number;
  buildHp?: number;
  buildPower?: number;
  buildMax?: number;
  flags?: string[];
  /** 妖を倒すたびに攻撃・最大HPがこの割合ずつ上がる（妖狩りの「喰らう」。重ねがけ、戦闘中だけ） */
  devour?: number;
}

export interface TierEffect {
  share: number;
  heal?: number; // 手番開始時に最大HPの割合を回復
  mat?: number; // 手番開始時の陣材
  move?: number;
  guard?: number; // どこでも被ダメ倍率
  huntMult?: number; // 中立・ガーディアンへの与ダメ倍率
  powerMult?: number; // 対ヒーローの与ダメ倍率
}
export interface TerritoryRule {
  name: string;
  desc: string;
  /** 自陣の中の補正。afflict は相手の陣地に踏み込んだ相手にかかる状態異常（turns は踏み込んだ手番を含む手番の数） */
  footing: { dealt?: number; taken?: number; heal?: number; enemyCost?: number; afflict?: { key: StatusKey; turns: number } };
  tiers: TierEffect[];
}

export interface RivalTemplate {
  agi: number;
  range: number;
  magic?: boolean;
  hpMult?: number;
  atkMult?: number;
  learn: Record<number, string>; // Lv → 技（might/iron/vigor/leech/focus/その他は flags）
  skill: RivalSkill;
}
export interface RivalSkill {
  name: string;
  mult: number;
  range: number;
  cd: number;
  magic?: boolean;
  splash?: number;
  delay?: number;
  leech?: number;
  guard?: boolean;
  heal?: number; // 最大HPの割合
  execute?: number;
}

/** 妖力の特性の効果（その段階までの特性を足し合わせる） */
export interface SurgeTraitMods {
  /** 術の範囲ダメージの割合（splash × (1+splash)） */
  splash?: number;
  /** 術の威力（mult × (1+skillPower)） */
  skillPower?: number;
  /** 術の待ち（手番） */
  cd?: number;
  /** 術の射程 */
  skillRange?: number;
  /** 術の遅延（相手の手番を遅らせる量）の割合 */
  delay?: number;
  /** 術の吸精（与ダメに対する割合）の加算 */
  leech?: number;
  /** 通常攻撃・術の与ダメの吸収（lifesteal）の加算 */
  lifesteal?: number;
  /** 移動力 */
  move?: number;
  /** 一撃離脱：攻撃の後にこのマス数まで動ける（1手番1回） */
  dash?: number;
  /** 同時に建っていられる数・1手番の建築回数 */
  buildMax?: number;
  buildPerTurn?: number;
  /** 本陣への与ダメ（割合。妖力の攻撃の上乗せ・術の威力の特性は本陣に乗らないので、本陣向けはこれで） */
  core?: number;
  /** 建物HP（割合）・攻撃の建物（櫓・藁人形・焙烙台・大筒）の威力（割合）。建てた時点で決まる */
  buildHp?: number;
  yagura?: number;
}
export interface SurgeTrait {
  id: string;
  name: string;
  desc: string;
  mods: SurgeTraitMods;
}

export interface BuildingDef {
  name: string;
  desc: string;
  cost: number;
  hp: number; // 基準HP（ヒーローの最大HP/140 を掛ける）
  def: number;
  dmg?: number; // 攻撃力に対する割合（櫓・落とし穴・狐火）
  range?: number;
  aura?: number; // 幟：与ダメ加算
  repair?: number; // 修繕小屋：本陣最大HPに対する割合
  near?: number; // 本陣から何マス以内に建てられるか
  heal?: number; // 鳥居：そばで手番開始すると最大HPの割合を回復
  inside?: boolean; // 自陣の中にしか建てられない
  every?: number; // 攻撃の建物：この手番ごとに1回撃つ（大筒）
  splash?: number; // 攻撃の建物：的の周りにこの割合（焙烙台）
  burn?: number; // 攻撃の建物：当てたダメージのこの割合の火傷（1手番）
  curse?: number; // 攻撃の建物：呪い・足止めの手番数（藁人形）
  /** 鉄皮を撃ち抜く（鉄砲狭間。無限防衛の敵の性質） */
  pierce?: boolean;
  /** 飛ぶ敵へのダメージの倍率（無限防衛の敵の性質） */
  flyMult?: number;
  /** 当てた敵の近くの敵へ跳ねる数と威力（雷太鼓） */
  chain?: number;
  chainMult?: number;
  /** 手番の最初に、このマス数以内の敵すべてに当てる（毒霧の壺） */
  pulse?: number;
}

function defaults() {
  return {
    /** プレイヤーの手番がこの回数を超えたら時間切れ負け */
    turnLimit: 120,
    /** 念のための打ち切り（CTBの時刻） */
    timeLimit: 60000,
    /** CTB：待ち = ceil(1000/(100+AGI)) × tick */
    tick: 20,
    dice: [1, 2, 3, 4],
    /**
     * 賽3つ（rules 2）：1〜6の賽を3つ振り、合計で移動（moveAt 以下なら 1・2・3、それより上は4）。
     * 役：ピンゾロ（1・1・1）・ゾロ目・シゴロ（4・5・6）・順目（1・2・3 / 2・3・4 / 3・4・5）。atk はその手番の与ダメ、free は建築の陣材が要らない
     * 術の賽：術・奥義を放つときに3つ振り、合計に応じて威力が上がる（3で+0・18で+span）。ゾロ目は zorome 倍
     */
    dice3: {
      moveAt: [8, 10, 12],
      high: 10,
      low: 9,
      roles: {
        pinzoro: { name: 'ピンゾロ', atk: 1.5, free: true },
        zorome: { name: 'ゾロ目', atk: 1.0, free: false },
        shigoro: { name: 'シゴロ', atk: 0.6, free: true },
        jun: { name: '順目', atk: 0, free: true },
      } as Record<string, { name: string; atk: number; free: boolean }>,
      skill: { span: 0.25, zorome: 1.5 },
    },
    /** ヒーロー全員の移動力の基礎（賽の目に足す。キャラ・装備の移動はこの上に足す） */
    moveBase: 1,
    overtime: { start: 3000, step: 500, add: 0.25 },
    homeHeal: 0.25,
    respawnTime: 200,
    /**
     * 敵の強さの基準曲線：ステージ s の想定育成（推奨戦力ちょうど・章内/章ボスの上乗せを除く）の HP・攻撃が、ステージ1の何倍か。
     * 章ごとに [1面, 10面]、章内は対数で直線補間。meta の想定育成から tools/sim/fit-curve.ts で作る（meta を変えたら作り直す）。
     * 敵の HP・防御・本陣は プレイヤーの攻撃の曲線（atk）で、敵の攻撃は プレイヤーのHPの曲線（hp）で伸びる＝何発で倒す/倒されるかが章で変わらない。
     */
    curve: {
      hp: [[1, 1.83], [2.26, 3.89], [4.46, 6.79], [7.43, 9.3], [9.99, 12.4], [13.22, 14.63], [14.7, 15.1], [15.12, 15.32], [15.33, 16.18], [16.2, 16.29]] as [number, number][],
      atk: [[1, 1.43], [1.69, 2.85], [3.25, 5.13], [5.51, 6.85], [7.29, 8.4], [8.78, 9.93], [9.99, 10.23], [10.24, 10.36], [10.36, 11.17], [11.18, 11.29]] as [number, number][],
    },
    /**
     * 基準曲線に掛ける敵の強さの倍率（HP・攻撃・防御・本陣のすべて。推奨戦力には meta の recTune で章ごとに合わせる）。chapter は1〜10章、stage は面ごとの上書き（章の倍率に掛ける）。
     * 熱心な遊び方で、主Lvが上がらずに素通りする章を硬く・2日以上止まる面を軽く（BALANCE.md 43章）。
     * 地図の隣接で遊ぶ順番は 5章→7・8章→6章→9章→10章（6章は7章の先）。この順で推奨戦力が下がらないように（BALANCE.md 46章）
     */
    /** 最初の戦場（1-1・チュートリアル）の妖と敵の武将の HP・攻撃の倍率。案内どおりに動いて戦闘不能にならないように（本陣は変えない。2026-11-02） */
    firstStageEnemy: 0.6,
    curveTune: {
      chapter: [1, 1, 1, 1, 0.93, 1.57, 0.99, 1.04, 1.64, 1.7] as number[],
      /** 敵だけに掛ける面ごとの倍率（推奨戦力には掛けない。BALANCE.md 43章）。敵と推奨戦力の両方に掛ける倍率は battle/stage-tune.ts */
      stage: { 61: 0.9, 69: 0.9, 73: 0.92, 77: 0.9, 79: 0.85 } as Record<number, number>,
    },
    /** 本陣（HP・防御は curve.atk 倍） */
    coreDef: { base: 24 },
    coreHp: {
      base: 140,
      perIndex: 4,
      /**
       * 章ごとの倍率。本陣を落とすのに要る攻撃回数（action-breakdown の「推定回数」）を章でそろえる（1〜2章は少し軽く）。
       * 以前は1戦の手番をそろえる値で、7章・9章が突出していた（2026-10 のテンポ調整で作り直し）
       */
      chapterMult: [0.58, 0.58, 0.68, 0.61, 0.72, 0.7, 0.84, 1, 0.95, 1.06],
      /** 砦・本城（章の9・10番目）だけの倍率。4章から徐々に上げて7章以降は1.5倍（攻撃を積んだ型が数手番で城を割らないように。BALANCE.md 26章） */
      castleMult: [1, 1, 1, 1.125, 1.25, 1.375, 1.5, 1.5, 1.5, 1.5],
    },
    /** 本陣への攻撃倍率（陣地の段階 0〜3）。最低段でも3〜4回、陣地を取れば2回ほどで落ちる */
    coreAssault: [0.75, 0.9, 1.1, 1.4],
    coreIntercept: 0.12,
    relayShare: 0.05,
    territoryDepth: 3,
    tilePaintBonus: 1,
    siege: { hits: 3, mult: 1.5 },
    ward: 0.7,
    /** 結界のある間に残す攻城系の上乗せ（城崩し・本陣への与ダメ・術の本陣倍率・攻城の印の「1を超えた分」に掛ける。1＝今のまま、0＝無効。BALANCE.md 54章） */
    wardSiege: 0.5,
    /** 試作（BALANCE.md 19章）：結界のある間、本陣のHPはこの割合より下がらない（0＝なし） */
    wardFloor: 0,
    /** 試作（20章）：結界のある間の本陣の迎撃の倍率（1＝今のまま） */
    wardIntercept: 1,
    /** 試作（20章）：本陣への倍率（城崩し・本陣への与ダメ・術の本陣倍率・結界が割れた後）を掛け算でなく足し算で重ねる */
    coreAdditive: false,
    /** 試作（20章）：本陣のHPがこの割合を下回るたびに、守備の妖が本陣の隣に湧く（空＝なし）。強さは盤上の中型の妖 × coreGuardMult */
    coreGuards: [] as number[],
    coreGuardMult: 1,
    /** 試作（20章）：ヒーローの1回の攻撃で本陣を削れるのは最大HPのこの割合まで（0＝なし） */
    coreHitCap: 0,
    /**
     * 番人（ガーディアン）が生きているうちに本陣を殴られると、番人が目を覚まして殴った相手を迎え撃つ（BALANCE.md 24章）。
     * 1手番の歩数（0＝なし）。持ち場から guardianRouseLeash マスまでしか離れず、相手が離れたら持ち場へ戻って落ち着く
     */
    guardianRouse: 1,
    guardianRouseLeash: 2,
    guardianRouseAtk: 1,
    /** 試作（20章）：相手の本陣からこのマス数以内に入っても番人が目を覚ます（0＝殴ったときだけ） */
    guardianRouseNear: 0,
    /** 試作（20章）：術に溜めが要るキャラ（spec.key）。術を選ぶとその手番は溜めて終わり、次の手番以降に撃てる */
    chargeSkill: [] as string[],
    /** 溜めが要るのは本陣を狙うときだけ */
    chargeCoreOnly: false,
    bossStrikeMult: 1.8,
    eliteStrikeMult: 1.5,
    campLeash: 2,
    enemy: { aggro: 2, steps: 1, roam: 2 },
    raider: { aggro: 3, steps: 2, roam: 4 },
    neutralResetTurns: 2,
    shrineHeal: 0.3,
    chestMat: 5,
    /** 宝箱の中身の重み（自分が開けたときだけ。陣材 chestMat に加えて1つ）。量は meta の CONFIG.rewards.chest */
    chestLoot: { zeni: 35, reidama: 20, equip: 30, omamori: 15 } as Record<ChestLoot, number>,
    material: { start: 4, base: 1, perTiles: 5, perRelay: 1, cap: 30 },
    /**
     * 妖力（戦闘中の成長。内部ID surge）。両陣営とも段階1（壱）から始まり、撃破ポイントを貯めて上がる。戦闘が終われば消える。
     * 表示名は label / stageNames だけ変えればよい（画面は battle/index.ts の surgeLabel・surgeStageName を使う）。
     */
    surge: {
      enabled: true,
      label: '妖力',
      stageNames: ['壱', '弐', '参'],
      /** 段階ごとの必要な累計ポイント（[段階1, 段階2, 段階3]） */
      thresholds: [0, 2, 5],
      /** ヒーローが倒されたときの妖力（BALANCE.md 26章）。none＝そのまま／one＝1段下がる／all＝壱・0点に戻る */
      koReset: 'all' as 'none' | 'one' | 'all',
      /** 撃破ポイント：中立（増援を含む）・相手ヒーロー・相手の建物。ガーディアン（番人・章ボス）は bossStages 段の即上昇 */
      points: { neutral: 1, hero: 2, building: 1 },
      bossStages: 1,
      /**
       * 段階ごとの効果（段階1からの累計）。atk・hp は能力の割合、heal は段階が上がった瞬間に最大HPの割合を回復、cd は術の待ち。
       * core は本陣への与ダメの上乗せ（atk の代わりに使う。atk の上乗せは本陣には乗らない）
       */
      stages: [
        { atk: 0, hp: 0, heal: 0, cd: 0, core: 0 },
        { atk: 0.12, hp: 0.12, heal: 0.25, cd: 0, core: 0 },
        { atk: 0.24, hp: 0.24, heal: 0.25, cd: 0, core: 0 },
      ] as { atk: number; hp: number; heal: number; cd: number; core: number }[],
      /**
       * 段階ごとに解放される特性（キー → 段階 → 特性）。キーは自分のキャラID、敵ヒーローは rival_<型>。
       * 段階2・3の特性は累計（参では弐の特性も効く）。名前は surge イベントに入る（カットイン用）
       */
      traits: {
        akari: {
          2: [{ id: 'akari_wa', name: '花火の輪', desc: '術の範囲ダメージ+25%', mods: { splash: 0.25 } }],
          3: [{ id: 'akari_dairin', name: '大輪', desc: '術の威力+30%・術の待ち-1', mods: { skillPower: 0.3, cd: -1 } }],
        },
        kazamaru: {
          2: [{ id: 'kazamaru_oikaze', name: '追い風', desc: '疾風の遅らせる量+50%', mods: { delay: 0.5 } }],
          3: [{ id: 'kazamaru_ichigeki', name: '一撃離脱', desc: '攻撃の後に一度だけ2マスまで動ける', mods: { dash: 2 } }],
        },
        suzune: {
          2: [{ id: 'suzune_tegiwa', name: '鬼の手際', desc: '同時に建てられる数+1・1手番の建築+1回・移動+1', mods: { buildMax: 1, buildPerTurn: 1, move: 1 } }],
          3: [{ id: 'suzune_onibushin', name: '鬼普請', desc: '建物HP+50%・攻撃の建物の威力+50%', mods: { buildHp: 0.5, yagura: 0.5 } }],
        },
        yuzuha: {
          2: [{ id: 'yuzuha_kawaki', name: '妖狐の渇き', desc: '狐火斬りの吸精+10%', mods: { leech: 0.1 } }],
          3: [{ id: 'yuzuha_issen', name: '狐火一閃', desc: '狐火斬りの威力+40%', mods: { skillPower: 0.4 } }],
        },
        rival_jutsu: {
          2: [{ id: 'rival_jutsu_sae', name: '妖術の冴え', desc: '術の威力+15%', mods: { skillPower: 0.15 } }],
          3: [{ id: 'rival_jutsu_noroi', name: '百鬼の呪', desc: '術の待ち-1', mods: { cd: -1 } }],
        },
        rival_bu: {
          2: [{ id: 'rival_bu_idaten', name: '韋駄天', desc: '移動+1', mods: { move: 1 } }],
          3: [{ id: 'rival_bu_kiki', name: '鬼気', desc: '術の威力+15%・与ダメの10%を吸収', mods: { skillPower: 0.15, lifesteal: 0.1 } }],
        },
        rival_fushin: {
          2: [{ id: 'rival_fushin_tegiwa', name: '築城の手際', desc: '同時に建てられる数+1・1手番の建築+1回', mods: { buildMax: 1, buildPerTurn: 1 } }],
          3: [{ id: 'rival_fushin_kenrou', name: '堅牢', desc: '建物HP+50%・攻撃の建物の威力+50%', mods: { buildHp: 0.5, yagura: 0.5 } }],
        },
      } as Record<string, Record<number, SurgeTrait[]>>,
      /** 敵ヒーローの型（rivals の番号＝章ごと）：術型・武型・建築型 */
      rivalTraits: ['fushin', 'fushin', 'bu', 'jutsu', 'bu', 'fushin', 'jutsu', 'bu', 'fushin', 'jutsu'],
      /**
       * 奥義（味方だけ。loadout のフラグ surge:ougi で使える＝専用ツリーの「奥義」（主Lv50で解放）を装着する）。
       * 妖力が最大の段階のとき、術の代わりに放てる（術の待ちは無視、封印中は不可）。放つと妖力は段階1・0点に戻る。
       * 術の威力 × mult、射程 + range。キャラごとの上乗せ：splash（範囲の割合の下限）・pierce（貫通+）・delay（遅らせる量+割合）・core（本陣への倍率）・leech（吸収+）
       */
      ougi: {
        mult: 2,
        /** 本陣に撃ったときの倍率（mult とキャラの core に掛ける。BALANCE.md 25章） */
        coreMult: 1,
        range: 1,
        chars: {
          akari: { name: '大輪百花', desc: '術の威力2倍・射程+1・周りにも同じ威力', splash: 1 },
          kazamaru: { name: '神風', desc: '術の威力2倍・射程+1・貫通+2・遅らせる量2倍', pierce: 2, delay: 1 },
          suzune: { name: '鬼哭破城', desc: '術の威力2倍・射程+1・本陣へさらに1.5倍', core: 1.5 },
          yuzuha: { name: '九尾一閃', desc: '術の威力2倍・射程+1・与ダメの50%を回復', leech: 0.3 },
        } as Record<string, { name: string; desc: string; splash?: number; pierce?: number; delay?: number; core?: number; leech?: number }>,
      },
    },
    /**
     * 敵の型（data/enemies.ts の type）。
     * heavy＝重装：鎧（armor 枚）がある間、物理の攻撃は taken 倍しか通らない。当たるたびに1枚剥がれる。魔法は鎧を無視する。素早さ agi（遅い）。
     * siege＝攻城：増援で出ると英雄を相手にせず、狙った陣営の本陣へ進む（左の脇から出たら自分、右なら敵）。本陣を最大HPの corePct ずつ削る。
     *   道をふさぐ建物は building 倍で壊し、ヒーローにふさがれたら hero 倍の力で殴る。増援の章 fromChapter から、増援の偶数面で出る
     */
    enemyTypes: {
      heavy: { armor: { small: 1, medium: 1, elite: 2, raider: 2, boss: 2 } as Record<string, number>, taken: 0.2, agi: -12, hp: 1.1 },
      siege: { corePct: 0.08, building: 2, hero: 0.6, hp: 1.3, steps: 2, fromChapter: 6 },
    },
    /** 撃破で入る陣材（中立・ガーディアン・ヒーロー） */
    killMat: { small: 1, medium: 2, elite: 3, boss: 6, raider: 3, hero: 4 } as Record<string, number>,
    /** AIが撃破を評価するときの価値（mch-dice の exp+gold 相当） */
    killValue: { small: 50, medium: 85, elite: 140, boss: 320, raider: 190 } as Record<string, number>,
    /** 自分のヒーローを倒したときの攻撃倍率など、技（flags）の値 */
    flags: {
      /** 風車守：手番の初めにこの確率で移動+1 */
      kazaguruma: 0.5,
      predator: 1.3, // 中立・ガーディアンへ
      conqueror: 1.2, // 自陣の中で
      momentum: 1.2, // 2マス以上動いた手
      riposte: 1.35, // 待機の次の攻撃
      execution: 1.25, // HP35%以下へ
      regen: 0.05, // 手番開始時、最大HPの割合
      sanctuary: { def: 0.25, heal: 0.05 }, // 自陣の中でDEF+25%（自分のDEFに対して）・回復
      siege: 1.25, // 本陣へ
      counter: 0.5, // bu_counter：隣から攻撃されたら通常攻撃のこの倍率で反撃
      senpuSplash: 0.4, // ougi:kazamaru_senpu：疾風の範囲（周囲も遅延）
      kekkaiBurn: 0.25, // ougi:yuzuha_kekkai：術のダメージのこの割合の火傷（1手番）
      kasaMin: 0.1, // comp:kasa（お供・傘の守り）：最大HPのこの割合以上のダメージを防ぐ（小さな攻撃では使わない）
      slowTurns: 1, // comp:slow（お供・足止め）：相手ヒーローにかける足止めの手番数
      /** 型のスキル（BALANCE.md 23章）：fushin_*＝守りの普請（スズネ）、kaeshi＝返し刀・duelist＝一騎討ち（ユズハ）、hitrun＝天狗の離れ技（カザマル）。akari_lantern2（灯し火の範囲2）は試作のみ */
      fushinRepair: 0.15, // fushin_repair：手番の最初に自分から2マス以内の自分の建物を最大HPのこの割合で直す
      fushinLead: 0.3, // fushin_lead：自分の隣の自分の建物は攻撃の建物の威力+この割合・被ダメ-この割合
      fushinGuard: 0.15, // fushin_guard：自分の建物の隣にいると被ダメ-この割合
      kaeshi: 1.0, // kaeshi：隣からの攻撃への反撃の倍率（bu_counter と重ならず大きい方）
      duelist: 1.2, // duelist：相手ヒーローへの与ダメ
      stride: { per: 0.025, max: 0.2 }, // stride（カザマルの勢い）：この手番に動いたマス数 × per の与ダメ上乗せ（上限 max。BALANCE.md 22章）
      hitrun: 2, // hitrun：攻撃の後にこのマス数まで動ける
      /** 装備の系統の固有（eq_*。meta の equip-lines）：eq_ignite＝術を当てた相手に与ダメのこの割合の火傷（1手番）、eq_bind＝通常攻撃で足止め（手番数）、
       * eq_stagger＝通常攻撃で次の行動を遅らせる（術の delay と同じ単位）、eq_cleave＝通常攻撃で的の周りにこの割合、eq_double＝通常攻撃の後にこの倍率で追撃 */
      eqIgnite: 0.4,
      eqBind: 2,
      eqStagger: 250,
      eqCleave: 0.5,
      eqDouble: 0.5,
      /** eq_knock＝通常攻撃で1マス押し出す（押せないときはこの割合の追加ダメージ）、eq_volley＝攻撃すると、的を射程に入れた自分の攻撃の建物がこの割合で撃つ */
      eqKnockWall: 0.25,
      eqKnockDelay: 200,
      eqVolley: 0.6,
      compBurnTurns: 1, // comp:burn（お供・火傷を付ける）：相手に付ける火傷の手番数（量は与ダメの割合）
      ougiPinch: 0.5, // shiki:pinch（式神の奥義）：HPがこの割合を下回ったとき
      ougiMidTurn: 6, // shiki:mid：この手番の始め
      ougiBlastRange: 2, // shiki:*:blast：自分からこのマス数以内の相手に
      ougiStatusTurns: 2, // shiki:*:burn：相手ヒーローに付ける火傷の手番数
    },
    /**
     * 状態異常。確率では発動しない（当たれば必ずかかる）。重ねがけせず、かかり直すと残り手番が長い方になる。
     * かかった側の手番の終わりに残り手番が1減る（自分の手番中にかかったらその手番も1と数える）。火傷は手番の最初にダメージ。
     * 鳥居のそば（自分の鳥居から1マス）ではかからず、手番の最初に治る。帰還マスでも治る。
     */
    status: {
      burnDmg: 0.06, // 火傷：手番の最初に最大HPのこの割合（狐火結界のように量が決まっているものはその量）
      bindMove: 1, // 足止め：移動-1
      curseHeal: 0.5, // 呪い：回復がこの倍率
      info: {
        burn: { name: '火傷', desc: '手番の最初にダメージ' },
        bind: { name: '足止め', desc: '移動-1' },
        seal: { name: '封印', desc: '術が使えない' },
        curse: { name: '呪い', desc: '回復が半分' },
        poison: { name: '毒', desc: '手番の最初にダメージ' },
        might: { name: '剛力', desc: '与ダメ+35%', good: true },
        ward: { name: '守り', desc: '被ダメ-40%', good: true },
        blessed: { name: '清め', desc: '状態異常にかからない', good: true },
      } as Record<StatusKey, { name: string; desc: string; good?: boolean }>,
      might: 1.35, // 剛力：与ダメ倍率
      ward: 0.6, // 守り：被ダメ倍率
    },
    /**
     * 戦闘アイテム。出撃前に2種類を選び（meta）、毎戦その数だけ支給。使っても手番は終わらない・1手番に1回。
     * turns は自分の手番の数（使った手番も1と数える）。回復薬の量は loadout.potions.heal（最大HPの割合）
     */
    items: {
      heal: { name: '回復薬', desc: 'HPを回復する', icon: 'potion' },
      power: { name: '力の薬', desc: '3手番、与ダメ+35%', icon: 'burst', turns: 3 },
      guard: { name: '守りの薬', desc: '3手番、被ダメ-40%', icon: 'shield', turns: 3 },
      swift: { name: '韋駄天の薬', desc: 'この手番の移動+3（賽を振る前か、振った後で動く前に）', icon: 'feather', move: 3 },
      purify: { name: '清め札', desc: '状態異常を治し、2手番かからなくなる', icon: 'scroll', turns: 2 },
      spirit: { name: '妖力の薬', desc: '撃破ポイント+1（妖力の段階が上がりやすくなる）', icon: 'skill', points: 1 },
    } as Record<ItemId, { name: string; desc: string; icon: string; turns?: number; move?: number; points?: number }>,
    /** AI（オート・シミュ）が回復薬を飲む目安：受けそうなダメージの後にこの割合を下回る／今この割合を下回っていて溢れが少ない */
    itemAiHeal: { afterHit: 0.35, below: 0.6 },
    /** キャラ固有の特性（chars.ts の passive）。meta 側で二重に入れないこと */
    passives: {
      // スズネ＝建築キャラ（大槌で建てる）、カザマル＝機動・武（移動+1 は基礎値）
      suzune: { coreMult: 1.15, buildRange: 1 },
    } as Record<string, { coreMult?: number; buildRange?: number }>,
    /** 建物。図面Lvごとに効果・HPが lvStep ずつ上がる */
    buildLvStep: 0.1,
    buildings: {
      yagura: { name: '櫓', desc: '持ち主の手番の最初に射程2の敵を撃つ', cost: 6, hp: 60, def: 6, dmg: 0.5, range: 2 },
      sakaku: { name: '竹柵', desc: '相手は通れない。道をふさぐ', cost: 2, hp: 90, def: 8 },
      nobori: { name: '幟', desc: '隣接マスにいると与ダメ+15%', cost: 4, hp: 40, def: 4, aura: 0.15 },
      otoshiana: { name: '落とし穴', desc: '相手から見えない。踏むと止まってダメージ', cost: 3, hp: 1, def: 0, dmg: 0.65 },
      shuzen: { name: '修繕小屋', desc: '本陣から3マス以内。手番の最初に本陣を修復', cost: 5, hp: 50, def: 6, repair: 0.04, near: 3 },
      torii: { name: '鳥居', desc: '自陣の中に建てる。復活の地点になり、そばで手番を始めると回復。そばでは状態異常にかからず、治る', cost: 6, hp: 70, def: 8, heal: 0.1, inside: true },
      kitsunebi: { name: '狐火灯籠', desc: '隣接する自陣マスに入った敵を燃やす', cost: 4, hp: 45, def: 4, dmg: 0.3 },
      // 攻撃の建物（持ち主の手番の最初に射程の敵を撃つ。櫓と同じく敵ヒーロー優先）
      wara: { name: '藁人形', desc: '射程2の敵1体を呪い（回復半分）、足を止める', cost: 4, hp: 32, def: 3, dmg: 0.1, range: 2, curse: 2 },
      horoku: { name: '焙烙台', desc: '射程2の敵とその周りに焙烙玉。燃やす', cost: 6, hp: 45, def: 5, dmg: 0.22, range: 2, splash: 0.5, burn: 0.4 },
      ozutsu: { name: '大筒', desc: '射程3。2手番に1回、敵を大きく撃つ', cost: 9, hp: 55, def: 6, dmg: 0.75, range: 3, every: 2 },
      // 本編クリア後に図面を作れる建物（無限防衛の建物と同じ数値）
      teppo: { name: '鉄砲狭間', desc: '射程3の敵を撃つ', cost: 7, hp: 60, def: 7, dmg: 0.42, range: 3, pierce: true, flyMult: 1.5 },
      toseki: { name: '投石台', desc: '射程3。2手番に1回、敵とその周りに石を落とす', cost: 8, hp: 55, def: 6, dmg: 0.55, range: 3, every: 2, splash: 0.6 },
      raiko: { name: '雷太鼓', desc: '射程2。雷が近くの敵2体に跳ねる', cost: 7, hp: 45, def: 5, dmg: 0.3, range: 2, chain: 2, chainMult: 0.7 },
      dokutsubo: { name: '毒霧の壺', desc: '手番の最初に、まわり1マスの敵すべてに毒のダメージ', cost: 5, hp: 40, def: 4, dmg: 0.2, pulse: 1 },
      hiya: { name: '石火矢', desc: '射程2の敵を撃ち、燃やす', cost: 6, hp: 45, def: 5, dmg: 0.3, range: 2, burn: 0.5, flyMult: 2 },
    } as Record<BuildingType, BuildingDef>,
    buildingOrder: ['yagura', 'sakaku', 'nobori', 'otoshiana', 'shuzen', 'torii', 'kitsunebi', 'wara', 'horoku', 'ozutsu', 'teppo', 'toseki', 'raiko', 'dokutsubo', 'hiya'] as BuildingType[],
    /** 建物HPの基準になるヒーローHP（建物HP = hp × 最大HP/これ） */
    buildHpBase: 140,
    /** 中立の基礎能力（1章）。章ごとに chapterGrowth、章内で indexGrowth */
    neutrals: {
      small: { hp: 40, atk: 18, def: 6, agi: 80 },
      medium: { hp: 75, atk: 26, def: 8, agi: 110 },
      elite: { hp: 100, atk: 32, def: 10, agi: 100 },
      boss: { hp: 190, atk: 40, def: 12, agi: 85 },
      raider: { hp: 150, atk: 38, def: 12, agi: 110 },
    } as Record<string, { hp: number; atk: number; def: number; agi: number }>,
    /** 中立の基礎能力に掛ける：HP・防御は curve.atk、攻撃は curve.hp。章内の番号で (1 + index × (番号-1)) */
    neutralGrowth: { index: 0.04 },
    /** 敵IDごとの個性（図鑑番号 % 4）：耐久・俊敏・剛力・均衡 */
    neutralFlavor: [
      { hp: 1.15, atk: 0.95, agi: 0 },
      { hp: 0.9, atk: 1, agi: 25 },
      { hp: 0.95, atk: 1.12, agi: -10 },
      { hp: 1, atk: 1, agi: 0 },
    ],
    /** ガーディアンの攻撃の型（章ごと）：fan=正面3マス, line=直線2マス, burst=HP半分で周囲 */
    bossPatterns: ['fan', 'line', 'burst'] as const,
    /** 増援（章内の番号 → 出る時刻）。時刻は CTB の time */
    waves: { 6: [1500], 7: [1400], 8: [1300, 2500], 9: [1300, 2400], 10: [1200, 2200] } as Record<number, number[]>,
    /** ガーディアン：章内の番号がこれ以上でエリートが番人、10番目は章ボス */
    guardianFrom: 7,
    /**
     * 防衛戦（ボスの国の三ノ陣＝中ボス戦）。敵ヒーロー・敵本陣は無く、頭目が率いる妖の群れが自本陣へ攻めてくる。建物の上限は buildMaxMult 倍。
     * 第0波＝準備（prepTurns 手番）。そのあと gap 手番ごとに第1〜第3波。波が来るたびに、生き残っている野良の妖も寝返って攻め手に加わる。
     * 第3波まで来て攻め手がいなくなれば勝ち、本陣が落ちれば負け。waves の 'leader' は頭目（章のエリートを番人と同じ強さで。準備中は盤に出ない）
     */
    defense: {
      chapters: [1, 2, 3, 4, 5, 6],
      /** ボスの国の三ノ陣（章の6番目）＝中ボス戦。7〜9番目は番人（エリート）が構える通常の合戦のまま */
      index: 6,
      prepTurns: 5,
      gap: 5,
      waves: [
        ['small', 'small', 'small', 'small'],
        ['medium', 'small', 'medium', 'small', 'small'],
        ['leader', 'elite', 'medium', 'medium', 'small', 'small'],
      ] as ('small' | 'medium' | 'elite' | 'leader')[][],
      /** 防衛戦の特別ルール：同時に建てられる建物の数の倍率 */
      buildMaxMult: 2,
      /** 防衛戦のAI（自分側の自動操作・シミュレーター用）：追いかける半径、建物の重み、種類ごとの数（上限3あたり） */
      ai: {
        chaseRadius: 5,
        /** 準備中に倒しに行く野良（本陣からこのマス数まで） */
        prepRadius: 5,
        /** 本陣からこのマス数より遠い所は1マスごとに leashWeight 点下げる */
        leash: 4,
        leashWeight: 25,
        yagura: 1.6,
        sakaku: 20,
        trap: 1.6,
        lantern: 1.2,
        costWeight: 0.8,
        cap: { yagura: 2, sakaku: 1.5, otoshiana: 1, kitsunebi: 1, nobori: 0.5, shuzen: 0.5, wara: 0.5, horoku: 1, ozutsu: 1, teppo: 1, toseki: 0.5, raiko: 1, hiya: 1 } as Partial<Record<BuildingType, number>>,
      },
      /** 攻め手の1手番の歩数 */
      steps: 2,
      /** 寝返り：波が来たとき、生き残っている野良の妖が攻め手になる */
      defect: true,
      /** 攻め手の強さ（ステージの中立の基準に掛ける） */
      hp: 1.5,
      atk: 1.3,
      /** 章ごとの攻め手の強さの倍率（chapters と同じ並び。battle-sim --meta-power で較正） */
      chapterMult: [0.62, 0.42, 0.8, 0.7, 1.05, 1.35],
      /** 章ごとの自本陣のHPの倍率（通常の戦いの本陣HPは敵ヒーローとの競争に合わせてあり、守る戦いには小さい章がある） */
      coreMult: [1.6, 2.6, 1, 1, 1, 1],
    },
    eliteGuardian: { hp: 1.5, atk: 1.1 },
    /** 敵ヒーロー */
    rival: {
      base: { hp: 112, atk: 37, def: 11 },
      /** Lv（表示と技の習得に使う。能力は curve で伸びる） */
      lvPerChapter: 9,
      lvInChapter: 9, // 章の1→10番目で上がるLv
      /** 1Lvあたりの速さの伸び */
      growth: { agi: 0.002 },
      /** might/iron/vigor の上乗せ */
      tech: { might: 0.1, iron: 0.15, vigor: 0.1 },
      /** 章ごとの HP・攻撃の倍率（敵ヒーローの型ごとの強さの差をならす。battle-sim --meta-power で較正） */
      chapterPower: [1.423, 0.873, 0.993, 0.901, 1.432, 1.311, 1.276, 1.406, 1.218, 1.18],
      /** 章内の番号ごとの HP・攻撃の上乗せ（1 + perIndex × (番号-1)） */
      perIndex: 0,
      /** 章ボス（10番目）の敵ヒーローとボスの HP・攻撃の倍率（章ごと。推奨1.0倍で勝率6〜7割の壁にする） */
      bossStage: [0.92, 1.31, 1.22, 1.255, 1.22, 1.3, 1.26, 1.39, 1.37, 1.34],
      /** 1章だけ章内の番号ごとに HP・攻撃を下げる（初見の手動で勝てるように。7〜9番目は番人が出るぶん下げる） */
      earlyPower: [0.75, 0.9, 0.92, 0.92, 0.95, 0.95, 0.8, 0.8, 0.8, 1],
      materialsStart: 4,
    },
    /**
     * 土地ギミック（上の難易度の戦場だけ）。perDifficulty[d] 種類を、面ごとに決まった並びから選び、1種類につき mirror した2マスに置く（両陣営に公平）。
     * 霊泉＝手番の最初に最大HPの heal 回復、神域＝立っていると与ダメ +dealt、泥濘＝通ると足止め bindTurns 手番、毒沼＝通ると毒（最大HPの dmg を turns 手番）
     */
    ground: {
      perDifficulty: [0, 1, 2, 4],
      spring: { name: '霊泉', desc: '手番の最初にHPが回復する', heal: 0.12 },
      sacred: { name: '神域', desc: '立っていると与ダメ+20%', dealt: 0.2 },
      mire: { name: '泥濘', desc: '通ると足止め（移動-1）', bindTurns: 2 },
      miasma: { name: '毒沼', desc: '通ると毒（手番の最初にダメージ）', dmg: 0.06, turns: 2 },
    },
    /** 上の難易度：1段ごとの敵の強化 */
    difficulty: { hp: 0.3, atk: 0.18, core: 0.25, rivalLv: 5 },
    rivals: [
      { agi: 92, range: 1, learn: { 3: 'predator', 6: 'leech', 9: 'regen', 15: 'might', 21: 'iron' }, skill: { name: '強打', mult: 1.5, range: 1, cd: 3 } },
      { agi: 99, hpMult: 1.15, atkMult: 1.1, range: 1, learn: { 3: 'might', 6: 'leech', 9: 'execution', 15: 'iron', 21: 'vigor' }, skill: { name: '水の守り', mult: 1.2, range: 1, cd: 3, guard: true, heal: 0.18 } },
      { agi: 104, range: 1, learn: { 3: 'predator', 5: 'iron', 8: 'momentum', 14: 'might', 20: 'leech' }, skill: { name: '鎌鼬', mult: 1.5, range: 1, cd: 3, splash: 0.55 } },
      { agi: 100, range: 2, magic: true, learn: { 3: 'focus', 6: 'regen', 9: 'siege', 15: 'might' }, skill: { name: '骨縛り', mult: 1.2, range: 2, cd: 3, delay: 300 } },
      { agi: 106, range: 1, learn: { 3: 'might', 6: 'leech', 9: 'execution', 11: 'iron', 18: 'vigor' }, skill: { name: '雪崩斬り', mult: 1.4, range: 1, cd: 3, execute: 1.45 } },
      { agi: 99, hpMult: 1.2, atkMult: 1.1, range: 1, learn: { 3: 'iron', 6: 'vigor', 9: 'leech', 12: 'might' }, skill: { name: '火車輪', mult: 1.2, range: 2, cd: 3, delay: 300 } },
      { agi: 124, range: 2, magic: true, learn: { 3: 'focus', 6: 'regen', 9: 'siege', 12: 'might' }, skill: { name: '怨霊波', mult: 1.3, range: 2, cd: 3, splash: 0.4 } },
      { agi: 130, range: 1, learn: { 3: 'momentum', 6: 'leech', 9: 'might', 12: 'execution' }, skill: { name: '天狗風', mult: 1.5, range: 1, cd: 3 } },
      { agi: 140, hpMult: 0.95, range: 1, learn: { 3: 'predator', 6: 'leech', 9: 'might', 12: 'siege' }, skill: { name: '金棒', mult: 1.5, range: 1, cd: 3, execute: 1.3 } },
      { agi: 140, range: 2, magic: true, learn: { 3: 'might', 6: 'leech', 9: 'execution', 12: 'iron' }, skill: { name: '百鬼夜行', mult: 1.3, range: 2, cd: 3, splash: 0.6 } },
    ] as RivalTemplate[],
    /**
     * 敵の武将の傾向（BALANCE.md 34章）。章内の番号 1〜9 に order を章ごとに1つずつずらして割り当てる（章ボスの面・防衛戦は傾向なし＝通常のAI）。
     * style は AI の重み（battle/ai.ts の AiStyle）、aggro は前へ出る強さ、buildMax は同時に建てられる数の上乗せ
     */
    rivalAi: {
      order: ['rush', 'builder', 'hunter', 'farmer'] as RivalAiKey[],
      types: {
        // 速攻の型（push 0.45・core 1.7・aggro 1.4）のままだと一律のAIより勝率が15ポイント下がった（推奨0.9倍）ので和らげた（-6ポイント）
        rush: { name: '本陣速攻', style: { push: 0.3, core: 1.2, neutral: 0.8, paint: 0.8, build: 0.7 }, aggro: 1.1 },
        builder: { name: '築城', style: { build: 2.2, paint: 1.4 }, buildMax: 2 },
        hunter: { name: '武将狙い', style: { hero: 1.7, risk: 0.8 } },
        farmer: { name: '妖狩り', style: { farm: true, neutral: 1.6, hero: 0.8 } },
      } as Record<RivalAiKey, { name: string; style: AiStyle; aggro?: number; buildMax?: number }>,
    },
    /**
     * ルールの版3（types.ts の RULES_VERSION 3）で変わる値。戦場の種類ごとに型の向き不向きが出るように（BALANCE.md 35章）。
     * 版は setup に入っているので、進行中の戦い・記録の再生（版2以下）は元の値のまま
     */
    v3: {
      /** 防衛戦：攻め手の強さの倍率（defense.chapterMult に掛ける）。建物・範囲の術で波をさばく型が得意、一騎討ち・迎え撃ちは不得意に */
      defenseMult: 1.4,
      /** 砦（章の9番目）：自分の手番の上限。超えたら時間切れ負け（速攻・攻城の得意、長引く型の不得意） */
      castleTurnLimit: 12,
      /**
       * 敵の武将の傾向の差し替え（rivalAi.types の style・aggro。本陣速攻と武将狙いを型の立ち回りと同じ強さに）と、傾向ごとの性質（traits。BALANCE.md 40章）。
       * traits は武将の能力に掛ける：hp・atk（HP・攻撃）、core（本陣への与ダメ）、hero（相手の武将への与ダメ）、neutral（妖への与ダメ）、
       * buildHp・buildPower（建物のHP・攻撃の建物の威力）、buildMax（同時に建てられる数の上乗せ。rivalAi.types の buildMax に足す）、flags（技の旗。fushin_lead なら本陣が建物に守られる）
       */
      rivalAi: {
        // 本陣速攻：軽装の攻城兵。本陣への与ダメ 1.8倍、HP 0.75倍・攻撃 0.9倍（先に本陣を割る型・武将を仕留める型が得意、長引く型が不得意）
        rush: { style: { push: 0.45, core: 1.7, neutral: 0.6, paint: 0.6, build: 0.5 }, aggro: 1.4, traits: { core: 1.8, hp: 0.75, atk: 0.9 } },
        // 築城：建物HP 2倍・攻撃の建物の威力 1.6倍・同時に建てられる数 +2（types の +2 と合わせて +4）・鬼の棟梁（本陣が建物に守られる・隣の建物が硬く強い）。建つ前に割る型が得意、長引く型が不得意
        builder: { traits: { buildHp: 2, buildPower: 1.6, buildMax: 2, flags: ['fushin_lead'] } },
        // 武将狙い：相手の武将への与ダメ 1.5倍、被ダメ 1.3倍（打たれ弱い）。守りの硬い型・反撃の型が得意、軽装で踏み込む型が不得意
        hunter: { style: { hero: 2.4, risk: 0.6 }, aggro: 1.3, traits: { hero: 1.5, taken: 1.3 } },
        // 妖狩り：妖への与ダメ 2倍・捕食者、妖を倒すたびに攻撃・最大HP +10%（喰らう）。先に本陣を取る型が得意、長引く型が不得意
        farmer: { traits: { neutral: 2, devour: 0.1, flags: ['predator'] } },
      } as Partial<Record<RivalAiKey, { style?: AiStyle; aggro?: number; traits?: RivalTraits }>>,
      /** 守りの普請（鬼の棟梁 fushin_lead）：自分の本陣も、range マス以内の自分の建物1つごとに被ダメ-per（上限 max）。建物で本陣を固める型の核（本陣の隣には建てられないので2マス） */
      fushinCoreGuard: { range: 2, per: 0.3, max: 0.6 },
      /** CTB の待ちを刻みの切り上げなしに（tick×1000/(100+AGI) の四捨五入）。速さ 150・234 で手番が一段増える崖をなくし、速さ1つずつが効くように（BALANCE.md 37章） */
      ctbFine: true,
      /**
       * 敵の武将の術は、相手の武将を狙うときは「構え」（BALANCE.md 39章）：術を選んだ手番は射程のマスに予告（telegraph）を出して終わり、
       * 次の手番に、構えたマスにいる相手の武将へ放つ（番人の大技と同じく、相手が手番を1回得るまで待つ）。誰もいなければ空振り。
       * 術の待ちは構えた手番から数える。本陣・建物・妖への術は今まで通り（構えなし）。
       * 武将より少し遅いだけで「何もできずに一撃で倒される」先手の崖をなくす。mult は放つときの威力の倍率
       */
      rivalCharge: { enabled: true, mult: 1.5 },
      /** 敵の武将の最初の手番を待ちの this 倍ぶん遅らせる（0＝なし）。自分が武将より少し遅いだけで先手を取られ、妖力の弐（HP+12%・回復）を先に取られる崖をなくす */
      rivalFirstDelay: 0.25,
      /** 敵の武将の HP・攻撃の倍率（章ごと。構え・最初の手番の遅れで下がった難しさを戻す。術の重い後半の章ほど） */
      rivalPower: [1, 1, 1, 1.05, 1.05, 1.05, 1.1, 1.1, 1.1, 1.1],
    },
    /** 敵ヒーローの普請（章 → 建てられる建物）。図面Lvは (章-1)/2 */
    rivalBuild: {
      unlock: [
        ['yagura', 'sakaku'],
        ['otoshiana'],
        ['nobori'],
        ['shuzen'],
        ['kitsunebi'],
        ['torii'],
        ['wara'],
        ['horoku'],
        ['ozutsu'],
      ] as BuildingType[][],
      maxCount: 3,
      maxPerChapters: 3, // 3章ごとに+1
      powerPerChapter: 0.04,
    },
    /** 陣地ルール（ステージごと）。段階の閾値は share */
    territoryRules: {
      wind: {
        name: '追い風',
        desc: '自陣の中で与ダメ+15%・被ダメ-15%',
        footing: { dealt: 1.15, taken: 0.85 },
        tiers: [{ share: 0.15, heal: 0.04 }, { share: 0.3, move: 1 }, { share: 0.5, guard: 0.9 }],
      },
      trade: {
        name: '建築路',
        desc: '陣地が広いほど陣材が増える',
        footing: { dealt: 1.1, taken: 0.9 },
        tiers: [{ share: 0.15, mat: 1 }, { share: 0.3, mat: 2 }, { share: 0.5, heal: 0.05 }],
      },
      moss: {
        name: '苔の加護',
        desc: '自陣の中で被ダメ-15%・手番開始時に回復',
        footing: { taken: 0.85, heal: 0.06 },
        tiers: [{ share: 0.15, huntMult: 1.2 }, { share: 0.3, move: 1 }, { share: 0.5, heal: 0.05 }],
      },
      frost: {
        name: '凍土',
        desc: '相手の陣地に入ると2マス分',
        footing: { dealt: 1.15, taken: 0.85, enemyCost: 2 },
        tiers: [{ share: 0.15, heal: 0.04 }, { share: 0.3, guard: 0.85 }, { share: 0.5, move: 1 }],
      },
      ash: {
        name: '焦土',
        desc: '相手の陣地に踏み込むと火傷（次の手番の最初にダメージ）',
        footing: { dealt: 1.2, afflict: { key: 'burn', turns: 2 } },
        tiers: [{ share: 0.15, heal: 0.05 }, { share: 0.3, powerMult: 1.15 }, { share: 0.5, move: 1 }],
      },
      tide: {
        name: '潮目',
        desc: '相手の陣地に入ると2マス分・陣材が増える',
        footing: { dealt: 1.1, enemyCost: 2 },
        tiers: [{ share: 0.15, mat: 1 }, { share: 0.3, move: 1 }, { share: 0.5, heal: 0.05 }],
      },
      mist: {
        name: '霊霧',
        desc: '自陣の中で被ダメ-20%・相手の陣地に踏み込んだ手番は封印（術が使えない）',
        footing: { taken: 0.8, afflict: { key: 'seal', turns: 1 } },
        tiers: [{ share: 0.15, heal: 0.04 }, { share: 0.3, huntMult: 1.2 }, { share: 0.5, guard: 0.85 }],
      },
      shrine: {
        name: '神域',
        desc: '自陣の中で与ダメ+10%・被ダメ-10%・回復・相手の陣地に踏み込むと呪い（回復が半分・2手番）',
        footing: { dealt: 1.1, taken: 0.9, heal: 0.04, afflict: { key: 'curse', turns: 2 } },
        tiers: [{ share: 0.15, powerMult: 1.15 }, { share: 0.3, heal: 0.04 }, { share: 0.5, move: 1 }],
      },
    } as Record<string, TerritoryRule>,
    /** 章ごとの陣地ルールの組み合わせ（章内で順番に使う） */
    chapterRules: [
      ['wind', 'trade'],
      ['tide', 'wind', 'trade'],
      ['moss', 'trade', 'wind'],
      ['shrine', 'moss', 'frost'],
      ['frost', 'wind', 'shrine'],
      ['ash', 'frost', 'trade'],
      ['mist', 'ash', 'moss'],
      ['wind', 'frost', 'mist', 'tide'],
      ['ash', 'shrine', 'mist'],
      ['shrine', 'ash', 'frost', 'mist', 'tide', 'moss', 'wind', 'trade'],
    ],
    /** 章ごとのレイアウト（章内で順番に使う） */
    chapterLayouts: [
      ['meadow', 'crossing'],
      ['crossing', 'river', 'meadow'],
      ['ruins', 'crossing', 'fort'],
      ['ruins', 'corridor', 'fort'],
      ['wide', 'corridor', 'meadow'],
      ['volcano', 'ruins', 'river'],
      ['corridor', 'fort', 'wide'],
      ['wide', 'volcano', 'crossing'],
      ['volcano', 'fort', 'corridor', 'river'],
      ['corridor', 'volcano', 'wide', 'ruins', 'fort'],
    ],
  };
}

export type BattleConfig = ReturnType<typeof defaults>;

/** 現在の設定。直接書き換えず withConfig / setConfig を使う */
export let CFG: BattleConfig = defaults();

type DeepPartial<T> = { [K in keyof T]?: T[K] extends (infer U)[] ? U[] : T[K] extends object ? DeepPartial<T[K]> : T[K] };
export type ConfigOverrides = DeepPartial<BattleConfig>;

function merge(base: any, over: any): any {
  if (over === undefined) return base;
  if (Array.isArray(over) || typeof over !== 'object' || over === null) return over;
  const out: any = Array.isArray(base) ? base.slice() : { ...base };
  for (const k of Object.keys(over)) out[k] = merge(base?.[k], over[k]);
  return out;
}

/** 設定を上書きする（シミュレーターの --config 用）。null で既定に戻す */
export function setConfig(overrides: ConfigOverrides | null): void {
  CFG = overrides ? merge(defaults(), overrides) : defaults();
}

/** overrides を当てた設定で fn を実行し、元に戻す */
export function withConfig<T>(overrides: ConfigOverrides, fn: () => T): T {
  const prev = CFG;
  CFG = merge(prev, overrides);
  try {
    return fn();
  } finally {
    CFG = prev;
  }
}

export const agiTicks = (agi: number) => Math.ceil(1000 / (100 + Math.max(0, agi)));
